import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  QueryKey,
  TeamDto,
  TeamListDto,
  TeamMemberListDto,
  UpdateTeamMembersDto,
  UpdateTeamMembersResponseDto,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';

export const TEAM_TITLE_MAX_LENGTH = 256;
export const TEAM_SUBTITLE_MAX_LENGTH = 256;

export const useGetTeam = (
  query?: Parameters<typeof api.team.teamControllerFindAll>[0]
) =>
  useQuery({
    queryKey: [QueryKey.Resource.Teams, QueryKey.Operation.List, query],
    queryFn: async () =>
      api.team.teamControllerFindAll(
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });

export const useGetTeamsInfiniteList = (
  query: Parameters<typeof api.team.teamControllerFindAll>[0],
  options?: Partial<
    UseInfiniteQueryOptions<TeamListDto, Error, InfiniteData<TeamListDto>>
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.Teams,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
    ],
    queryFn: async ({ pageParam }) =>
      api.team.teamControllerFindAll({
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useGetSingleTeam = (
  teamUuid: string | undefined,
  options?: Partial<UseQueryOptions<TeamDto, Error, TeamDto>>
) =>
  useQuery({
    ...options,
    enabled: !!teamUuid,
    queryKey: [
      QueryKey.Resource.Teams,
      QueryKey.Resource.TeamsSingle,
      teamUuid,
    ],
    queryFn: async () => api.team.teamControllerFindOne(teamUuid || ''),
    placeholderData: keepPreviousData,
  });

export const useGetTeamMembers = (
  teamId?: string,
  query?: Parameters<typeof api.team.teamControllerGetMembers>[1]
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.Teams,
      QueryKey.Resource.Members,
      teamId,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
    ],
    enabled: !!teamId,
    queryFn: async () =>
      api.team.teamControllerGetMembers(
        teamId ?? '',
        query || {
          page: DEFAULT_PAGE,
          size: MAXIMUM_PAGINATION,
        }
      ),
  });

export const useGetTeamMembersInfiniteList = (
  teamId: string,
  query?: Parameters<typeof api.team.teamControllerGetMembers>[1],
  options?: Partial<
    UseInfiniteQueryOptions<
      TeamMemberListDto,
      Error,
      InfiniteData<TeamMemberListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    enabled: !!teamId && options?.enabled !== false,
    queryKey: [
      QueryKey.Resource.Teams,
      QueryKey.Resource.Members,
      teamId,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
    ],
    queryFn: async ({ pageParam }) =>
      api.team.teamControllerGetMembers(teamId, {
        ...query,
        page: pageParam as number,
      }),
    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export type UseUpdateTeamMembersOptions = UseMutationOptions<
  UpdateTeamMembersResponseDto,
  Error,
  UpdateTeamMembersDto
> & {
  teamId: string;
};

export const useUpdateTeamMembers = ({
  teamId,
  ...options
}: UseUpdateTeamMembersOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (data: UpdateTeamMembersDto) =>
      api.team.teamControllerUpdateTeamMembers(teamId, data),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Teams],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.TeamMembers],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useTeamTaskCount = (
  teamId: string | undefined,
  query?: Parameters<typeof api.team.teamControllerGetTaskCount>[1]
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.Teams,
      QueryKey.Resource.TaskCount,
      teamId,
      query,
    ],
    enabled: !!teamId,
    queryFn: async () =>
      api.team.teamControllerGetTaskCount(teamId || '', query),
  });
